/**
 * The master's billing (Phase 9.5), shaped exactly as GET /master/billing
 * answers.
 *
 * Nothing on the screen writes a plan. Checkout, a plan change and a
 * cancellation ask the payment provider; only the provider's webhook changes
 * the subscription, so this summary is always the plan actually in force.
 */
import type { PlanFeature, PlanTier } from '@/shared/config/plans';

import { api } from './http';

export type ApiBillingProvider = 'paddle' | 'freemius';

export type ApiSubscriptionStatus = 'active' | 'past_due' | 'cancelled';

export type ApiBillingPeriod = 'monthly' | 'annual';

export type ApiBillingCurrency = 'UAH' | 'EUR';

/** A paid plan, period and currency; what checkout and a plan change take. */
export type ApiPlanChoice = {
  plan: Exclude<PlanTier, 'free'>;
  period: ApiBillingPeriod;
  currency: ApiBillingCurrency;
};

export type ApiBillingSummary = {
  /** Null when no provider is configured: nothing is for sale. */
  provider: ApiBillingProvider | null;
  tier: PlanTier;
  /** From the `plans` table — the same list the API enforces. */
  features: PlanFeature[];
  /** `provider` pays for it, `studio` grants it, `free` has neither. */
  source: 'provider' | 'studio' | 'free';
  /** False for a studio member: the finances are the owner's. */
  isPayer: boolean;
  subscription: {
    provider: ApiBillingProvider;
    status: ApiSubscriptionStatus;
    period: ApiBillingPeriod;
    currentPeriodEnd: string | null;
    /** Set when the subscription ends at the close of the paid period. */
    cancelAt: string | null;
    /** Set while a failed renewal is in its grace period. */
    graceEndsAt: string | null;
  } | null;
  usage: {
    bookingsThisMonth: number;
    /** Null means unlimited. */
    bookingsLimit: number | null;
    photos: number;
    photosLimit: number | null;
    seats: number;
  };
  /** Exactly the combinations that have a price id; empty for a non-payer. */
  offers: { tier: PlanTier; period: ApiBillingPeriod; currency: ApiBillingCurrency }[];
};

export type ApiCheckoutSession = { provider: ApiBillingProvider; url: string };

export const billingSummaryKey = ['master', 'billing'] as const;

export const fetchBillingSummary = () => api.get<ApiBillingSummary>('/master/billing');
